/*
  Kiwi Ears · product timeline (Rene 2026-08-23; horizontal rail after
  dimaac.com/projects/h-scroll, same day).

  Three jobs, in the order they degrade:

    1. the rail. All years and cards sit in one horizontal track inside a
       native overflow-x view. This is the only job that matters, and it works
       with no JS at all: the strip is simply swipeable.
    2. the pin. On desktop, GSAP ScrollTrigger pins the band and turns vertical
       scroll into horizontal travel — the dimaac reference. ease:'none' on the
       pan, because anything else breaks the 1:1 scroll-to-position mapping.
       Skipped entirely under prefers-reduced-motion and below 1024px, where
       the native strip stays in charge. The two modes are exclusive: either
       the view scrolls or the track is transformed, never both, because two
       competing origins make the year readout lie.
    3. the background. A WebGL cross-fade between the years' photographs,
       driven by whichever year marker is nearest the viewport's left third. If
       WebGL is missing or the context is lost, the <img> stack underneath is
       shown instead and cross-faded with opacity — same pictures, same timing.

  Lenis is already bound to ScrollTrigger in assets/app.js (lenis.on('scroll',
  ScrollTrigger.update) plus gsap.ticker driving lenis.raf), so there is no
  scrollerProxy here. Adding a second one would double-drive the scroller.

  The pin is created for a section that may sit below other ScrollTriggers, so
  it carries a refreshPriority — refresh runs in creation order otherwise, and
  a pin refreshed out of page order gets its spacing wrong.
*/
(function () {
  if (window.__kiwiTimelineReady) return;
  window.__kiwiTimelineReady = true;

  var root = document.querySelector('[data-timeline]');
  if (!root) return;

  var view = root.querySelector('[data-tl-view]');
  var track = root.querySelector('[data-tl-track]');
  var groups = [].slice.call(root.querySelectorAll('[data-tl-group]'));
  var tabs = [].slice.call(root.querySelectorAll('[data-tl-year]'));
  var shots = [].slice.call(root.querySelectorAll('[data-tl-shot]'));
  var pctEl = root.querySelector('[data-tl-pct]');
  var count = groups.length;
  if (!view || !track || !count) return;

  var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var active = 0;

  /* --------------------------------------------------------- 1. position */

  /* How far the track can travel. Read fresh every time it is needed — the
     cards never change width after load, but the viewport does. */
  function maxPan() {
    return Math.max(0, track.scrollWidth - view.clientWidth);
  }

  /* Which year the reader is IN at pan offset x: the last group whose start
     has crossed a line a third of the way into the viewport. A third, not the
     edge — the year should change when its marker is well arrived, not the
     instant one pixel of it appears. */
  function yearAt(x) {
    var line = x + view.clientWidth * 0.34;
    var i = 0;
    for (var n = 0; n < count; n++) {
      if (groups[n].offsetLeft <= line) i = n;
    }
    return i;
  }

  function setPct(p) {
    if (pctEl) pctEl.textContent = '(' + Math.round(Math.max(0, Math.min(1, p)) * 100) + '%)';
  }

  function setYear(i) {
    i = Math.max(0, Math.min(count - 1, i));
    if (i === active) return;
    active = i;

    tabs.forEach(function (t, n) {
      if (n === i) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });

    /* The year in the URL, so a position can be linked to and survives a
       reload. replaceState, not push — travelling a rail should not fill the
       back button with five entries. */
    var y = tabs[i] && tabs[i].textContent.trim();
    if (y) {
      try { history.replaceState(null, '', '#year-' + y); } catch (e) {}
    }

    paint(i);
  }

  /* Both modes funnel through here: x is the current pan in px, however it
     was produced. */
  function onPan(x) {
    var m = maxPan();
    setPct(m ? x / m : 0);
    setYear(yearAt(x));
  }

  /* -------------------------------------------------------- 2. the modes */

  var jumpTo = null;
  var pinnedMode = false;

  /* Native mode is the floor: the view scrolls itself. The listener is wired
     unconditionally but inert while pinned — the pin sets overflow hidden and
     scrollLeft stays 0, and guarding on the flag keeps a stray scroll event
     (an anchor jump, a find-in-page) from fighting the pin's readout. */
  view.addEventListener('scroll', function () {
    if (pinnedMode) return;
    onPan(view.scrollLeft);
  }, { passive: true });

  function armNative() {
    root.setAttribute('data-tl-mode', 'native');
    /* tabindex only in this mode — a focusable div that cannot scroll is a
       dead tab stop, and in pinned mode the view cannot scroll. */
    view.setAttribute('tabindex', '0');
    jumpTo = function (i, instant) {
      var x = Math.min(groups[i].offsetLeft, maxPan());
      if (view.scrollTo) view.scrollTo({ left: x, behavior: instant || still ? 'auto' : 'smooth' });
      else view.scrollLeft = x;
    };
  }

  function armPin() {
    var g = window.gsap, ST = window.ScrollTrigger;
    if (!g || !ST || still) return false;
    if (window.matchMedia && window.matchMedia('(max-width: 1023px)').matches) return false;
    if (maxPan() <= 0) return false;

    pinnedMode = true;
    root.setAttribute('data-tl-mode', 'pinned');
    view.scrollLeft = 0;

    /* ease:'none' is not a taste decision — with anything else the scroll
       position and the horizontal position drift apart and the rail feels
       like it is fighting the wheel. scrub:true, not a number: Lenis already
       smooths the scroll itself, and a numeric scrub on top double-smooths
       the travel into mush. */
    var tween = g.to(track, {
      x: function () { return -maxPan(); },
      ease: 'none',
      scrollTrigger: {
        trigger: root,
        start: 'top top',
        /* one px of vertical scroll per px of horizontal travel, floored at a
           screen so a short rail still pins long enough to read */
        end: function () { return '+=' + Math.max(maxPan(), window.innerHeight); },
        pin: true,
        scrub: true,
        invalidateOnRefresh: true,
        refreshPriority: 1,
        onUpdate: function (self) { onPan(self.progress * maxPan()); }
      }
    });
    var st = tween.scrollTrigger;

    jumpTo = function (i, instant) {
      var m = maxPan();
      var frac = m ? Math.min(groups[i].offsetLeft, m) / m : 0;
      var y = st.start + (st.end - st.start) * frac;
      if (instant) { st.scroll(y); ST.update(); return; }
      if (window.lenis) window.lenis.scrollTo(y, { duration: 0.9 });
      else window.scrollTo({ top: y, behavior: 'smooth' });
    };

    /* Photographs and fonts change the track's width expectations; refresh
       once they are in so the travel is measured against the real layout. */
    ST.refresh();
    return true;
  }

  tabs.forEach(function (t, n) {
    t.addEventListener('click', function () {
      /* setYear() FIRST, then travel. The scroll takes ~0.9s and the pan's
         onUpdate is what would otherwise change the year — a tab clicked
         while pinned sat dead for the whole ride. setYear is idempotent, so
         the onUpdate that arrives as the scroll lands is a no-op and the tab
         lights up on the press instead. */
      setYear(n);
      if (jumpTo) jumpTo(n);
    });
  });

  /* Deep link: /pages/about#year-2024 opens on that year. */
  (function fromHash() {
    var m = /^#year-(\d{4})$/.exec(window.location.hash || '');
    if (!m) return;
    tabs.forEach(function (t, n) { if (t.textContent.trim() === m[1]) active = n; });
    tabs.forEach(function (t, n) {
      if (n === active) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
  })();

  /* ------------------------------------------------------- 3. background */

  var gl = null, glState = null;
  var canvas = root.querySelector('[data-tl-canvas]');
  var fallback = root.querySelector('[data-tl-fallback]');
  var MAX_DPR = 1.75;

  var VERT =
    'attribute vec2 p;varying vec2 uv;void main(){uv=p*0.5+0.5;gl_Position=vec4(p,0.0,1.0);}';

  /* The dissolve. Not a plain mix(): a value-noise field offsets the threshold
     per pixel so the two photographs break into each other instead of ghosting
     through one another, which is what a straight opacity fade looks like and
     is exactly what the <img> fallback already does. `cover` keeps each texture
     filling the band whatever its aspect, so a portrait photograph does not
     letterbox. */
  var FRAG = [
    'precision mediump float;',
    'varying vec2 uv;',
    'uniform sampler2D a;uniform sampler2D b;',
    'uniform float t;uniform vec2 res;uniform vec2 ar;uniform vec2 br;',
    'float h(vec2 v){return fract(sin(dot(v,vec2(12.9898,78.233)))*43758.5453);}',
    'float noise(vec2 v){vec2 i=floor(v);vec2 f=fract(v);f=f*f*(3.0-2.0*f);',
    ' return mix(mix(h(i),h(i+vec2(1.0,0.0)),f.x),mix(h(i+vec2(0.0,1.0)),h(i+vec2(1.0,1.0)),f.x),f.y);}',
    'vec2 cover(vec2 c,vec2 img){',
    ' float sa=res.x/res.y;float si=img.x/img.y;',
    /* crop = the sampled range SHRINKS on the overflowing axis, so multiply.
       Dividing pushed UVs past [0,1] and CLAMP_TO_EDGE smeared the edge pixels
       across the band — visibly stretched photographs. */
    ' vec2 sc=sa>si?vec2(1.0,si/sa):vec2(sa/si,1.0);',
    ' return (c-0.5)*sc+0.5;}',
    'void main(){',
    ' float n=noise(uv*vec2(9.0,5.0));',
    /* widen the threshold band so the edge is soft rather than a hard wipe */
    ' float m=smoothstep(t-0.34,t+0.34,mix(0.5,n,0.55));',
    ' vec4 ca=texture2D(a,cover(uv,ar));',
    ' vec4 cb=texture2D(b,cover(uv,br));',
    ' gl_FragColor=mix(ca,cb,1.0-m);}'
  ].join('\n');

  function compile(g, type, src) {
    var sh = g.createShader(type);
    g.shaderSource(sh, src);
    g.compileShader(sh);
    if (!g.getShaderParameter(sh, g.COMPILE_STATUS)) { g.deleteShader(sh); return null; }
    return sh;
  }

  function initGL() {
    if (!canvas || shots.length < 2) return false;
    var g;
    try {
      g = canvas.getContext('webgl', { antialias: false, alpha: false, depth: false }) ||
          canvas.getContext('experimental-webgl', { antialias: false, alpha: false, depth: false });
    } catch (e) { return false; }
    if (!g) return false;

    var vs = compile(g, g.VERTEX_SHADER, VERT);
    var fs = compile(g, g.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return false;
    var prog = g.createProgram();
    g.attachShader(prog, vs); g.attachShader(prog, fs); g.linkProgram(prog);
    if (!g.getProgramParameter(prog, g.LINK_STATUS)) return false;
    g.useProgram(prog);

    /* Image rows arrive top-down; GL's texture origin is bottom-left. Without
       this every photograph renders upside down — set once, it applies to all
       texImage2D calls on this context. */
    g.pixelStorei(g.UNPACK_FLIP_Y_WEBGL, 1);

    var buf = g.createBuffer();
    g.bindBuffer(g.ARRAY_BUFFER, buf);
    g.bufferData(g.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), g.STATIC_DRAW);
    var loc = g.getAttribLocation(prog, 'p');
    g.enableVertexAttribArray(loc);
    g.vertexAttribPointer(loc, 2, g.FLOAT, false, 0, 0);

    /* One texture per year, uploaded once the <img> has decoded. A year whose
       photograph has not arrived yet keeps the previous one rather than
       flashing black. */
    var texes = shots.map(function () { return null; });
    var sizes = shots.map(function () { return [1, 1]; });

    function upload(i) {
      var img = shots[i];
      if (!img || !img.complete || !img.naturalWidth) return;
      var tex = g.createTexture();
      g.bindTexture(g.TEXTURE_2D, tex);
      g.texParameteri(g.TEXTURE_2D, g.TEXTURE_WRAP_S, g.CLAMP_TO_EDGE);
      g.texParameteri(g.TEXTURE_2D, g.TEXTURE_WRAP_T, g.CLAMP_TO_EDGE);
      g.texParameteri(g.TEXTURE_2D, g.TEXTURE_MIN_FILTER, g.LINEAR);
      g.texParameteri(g.TEXTURE_2D, g.TEXTURE_MAG_FILTER, g.LINEAR);
      try { g.texImage2D(g.TEXTURE_2D, 0, g.RGBA, g.RGBA, g.UNSIGNED_BYTE, img); }
      catch (e) { return; }
      texes[i] = tex;
      sizes[i] = [img.naturalWidth, img.naturalHeight];
    }

    shots.forEach(function (img, i) {
      if (img.complete) upload(i);
      else img.addEventListener('load', function () { upload(i); draw(); }, { once: true });
    });

    gl = g;
    glState = {
      prog: prog, texes: texes, sizes: sizes,
      u: {
        a: g.getUniformLocation(prog, 'a'), b: g.getUniformLocation(prog, 'b'),
        t: g.getUniformLocation(prog, 't'), res: g.getUniformLocation(prog, 'res'),
        ar: g.getUniformLocation(prog, 'ar'), br: g.getUniformLocation(prog, 'br')
      },
      from: 0, to: 0, mix: 1
    };

    canvas.addEventListener('webglcontextlost', function (e) {
      e.preventDefault();
      gl = null; glState = null;
      if (fallback) fallback.style.opacity = '1';
      if (canvas) canvas.style.opacity = '0';
    }, false);

    resize();
    if (fallback) fallback.style.opacity = '0';
    canvas.style.opacity = '1';
    return true;
  }

  function resize() {
    if (!gl || !canvas) return;
    var dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    var w = Math.round(canvas.clientWidth * dpr), h = Math.round(canvas.clientHeight * dpr);
    if (!w || !h) return;
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
    gl.viewport(0, 0, w, h);
    gl.uniform2f(glState.u.res, w, h);
    draw();
  }

  function draw() {
    if (!gl || !glState) return;
    var st = glState;
    var ta = st.texes[st.from], tb = st.texes[st.to];
    if (!ta && !tb) return;
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, ta || tb); gl.uniform1i(st.u.a, 0);
    gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, tb || ta); gl.uniform1i(st.u.b, 1);
    var sa = st.sizes[st.from], sb = st.sizes[st.to];
    gl.uniform2f(st.u.ar, sa[0], sa[1]);
    gl.uniform2f(st.u.br, sb[0], sb[1]);
    gl.uniform1f(st.u.t, st.mix);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  /* paint(i) is the one entry point the rest of the file uses; whether that
     ends up in a shader or in opacity is this function's business alone. */
  function paint(i) {
    var g = window.gsap;
    if (gl && glState) {
      if (glState.to === i) return;
      glState.from = glState.to;
      glState.to = i;
      glState.mix = 0;
      if (g && !still) {
        g.killTweensOf(glState);
        g.to(glState, { mix: 1, duration: 0.9, ease: 'power2.inOut', onUpdate: draw, onComplete: draw });
      } else { glState.mix = 1; draw(); }
      return;
    }
    /* fallback: cross-fade the <img> stack */
    shots.forEach(function (img, n) {
      var on = n === i ? 1 : 0;
      if (g && !still) g.to(img, { opacity: on, duration: 0.5, ease: 'power2.inOut', overwrite: true });
      else img.style.opacity = String(on);
    });
  }

  /* ------------------------------------------------------------- arm it */

  function arm() {
    if (!initGL() && fallback) {
      /* no WebGL: the img stack is the background, and the first one is
         already visible from the markup */
      if (canvas) canvas.style.display = 'none';
    }
    if (!armPin()) armNative();
    paint(active);
    /* A deep link picked the year before the travel existed, so the readout
       said 2024 while the rail sat at 2021. Put the rail where the year is.
       Instantly, not smoothly: this is a page load, and animating the
       viewport away from where the browser put it is the behaviour
       scroll-restoration exists to prevent. */
    if (active > 0 && jumpTo) jumpTo(active, true);
    window.addEventListener('resize', function () {
      resize();
    }, { passive: true });
  }

  if (document.readyState === 'complete') arm();
  else window.addEventListener('load', arm);
})();
